import { useCallback, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DEEP_ANALYSIS_LABEL } from '@/hooks/useAnalysisSet';
import useMatter from '@/hooks/useMatter';

interface DeepAnalysisCostConfirmationOptions {
  documentCount?: number;
  tokenCount?: number;
  estimatedCostUsd?: number;
  estimateNote?: string;
  matterName?: string;
  scopeLabel?: string;
}

interface PendingConfirmation extends DeepAnalysisCostConfirmationOptions {
  resolve: (confirmed: boolean) => void;
}

const numberFormatter = new Intl.NumberFormat();
const compactNumberFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 1,
  notation: 'compact',
});
const currencyFormatter = new Intl.NumberFormat(undefined, {
  currency: 'USD',
  currencyDisplay: 'narrowSymbol',
  maximumFractionDigits: 2,
  style: 'currency',
});
// Conservative rough estimate from historical KG-enabled matters. Tracked LLM usage
// lands around ~$20/M weighted and ~$65/M at p75 against processed source tokens.
const ROUGH_DEEP_ANALYSIS_COST_PER_1M_SOURCE_TOKENS_USD = 50;

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return `${numberFormatter.format(count)} ${count === 1 ? singular : plural}`;
}

function buildScopeLabel(options: DeepAnalysisCostConfirmationOptions) {
  if (options.scopeLabel) return options.scopeLabel;
  if (options.documentCount !== undefined) {
    return pluralize(options.documentCount, 'file');
  }
  return 'the selected files';
}

function buildTokenLabel(tokenCount?: number) {
  if (!tokenCount || tokenCount <= 0) return null;
  return `~${compactNumberFormatter.format(Math.round(tokenCount))} source tokens`;
}

function buildCostLabel(options: DeepAnalysisCostConfirmationOptions) {
  const estimatedCost = options.estimatedCostUsd
    ?? (
      options.tokenCount && options.tokenCount > 0
        ? (options.tokenCount / 1_000_000) * ROUGH_DEEP_ANALYSIS_COST_PER_1M_SOURCE_TOKENS_USD
        : null
    );
  if (!estimatedCost || estimatedCost <= 0) return null;
  if (estimatedCost < 0.01) return '<$0.01';
  return `~${currencyFormatter.format(estimatedCost)}`;
}

export function useDeepAnalysisCostConfirmation() {
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const { matter } = useMatter();

  const confirmDeepAnalysisCost = useCallback((options: DeepAnalysisCostConfirmationOptions = {}) => (
    new Promise<boolean>((resolve) => {
      setPendingConfirmation({ ...options, resolve });
    })
  ), []);

  const closeConfirmation = useCallback((confirmed: boolean) => {
    const resolver = pendingConfirmation?.resolve;
    setPendingConfirmation(null);
    resolver?.(confirmed);
  }, [pendingConfirmation]);

  const deepAnalysisCostConfirmationDialog = useMemo(() => {
    const scopeLabel = pendingConfirmation
      ? buildScopeLabel(pendingConfirmation)
      : '';
    const tokenLabel = pendingConfirmation
      ? buildTokenLabel(pendingConfirmation.tokenCount)
      : null;
    const costLabel = pendingConfirmation
      ? buildCostLabel(pendingConfirmation)
      : null;
    const estimateItems = [scopeLabel, tokenLabel, costLabel].filter(Boolean);
    const estimateDetails = estimateItems.length > 1;
    const estimateNote = pendingConfirmation?.estimateNote
      ?? (estimateDetails ? 'Estimated usage is indicative and may differ from final usage.' : null);
    const billingMatterName = pendingConfirmation?.matterName ?? matter?.name;

    return (
      <Dialog
        open={Boolean(pendingConfirmation)}
        onOpenChange={(open) => {
          if (!open) closeConfirmation(false);
        }}
      >
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Add to {DEEP_ANALYSIS_LABEL}?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm leading-6 text-slate-700">
            <p>
              Crimson will build a consolidated entity graph, chronologies, and deeper
              document analysis for the selected files. This will incur additional
              token costs billed to this matter
              {billingMatterName ? (
                <>
                  : <span className="font-medium text-slate-900">{billingMatterName}</span>
                </>
              ) : null}
              .
            </p>
            <div className="min-w-0 rounded-md bg-slate-50 px-3 py-2 text-slate-700">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Estimated added work
              </div>
              <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-700 sm:flex-nowrap">
                {estimateItems.map((item, index) => (
                  <span key={item} className="flex items-center gap-x-2 whitespace-nowrap">
                    {index > 0 && <span className="text-slate-300">•</span>}
                    <span>
                      {item}
                    </span>
                  </span>
                ))}
              </div>
            </div>
            {estimateNote && (
              <p className="text-xs leading-5 text-slate-500">
                {estimateNote}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => closeConfirmation(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => closeConfirmation(true)}>
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }, [closeConfirmation, matter?.name, pendingConfirmation]);

  return {
    confirmDeepAnalysisCost,
    deepAnalysisCostConfirmationDialog,
  };
}
